import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LogoIcon from '../common/LogoIcon';

const { FiMenu, FiX } = FiIcons;

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'The Stay', path: '/stay' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Location', path: '/location' },
    { name: 'Rates', path: '/rates' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-brand-cream h-[100px] flex items-center shadow-sm">
      <div className="container mx-auto px-6 lg:px-12 flex items-center justify-between w-full max-w-[1400px]">
        {/* Logo Section - Reduced gap to 0.5 for an ultra-tight lockup */}
        <Link to="/" className="flex items-center gap-0.5 shrink-0 group">
          <LogoIcon className="w-[72px] h-[72px] transition-transform duration-500 group-hover:scale-110" />
          <div className="flex flex-col justify-center transition-transform duration-300 group-hover:translate-x-0.5">
            <span className="font-serif text-[28px] tracking-[0.05em] uppercase text-brand-olive leading-none mt-1">
              R. VENUE
            </span>
            <span className="font-script text-[32px] text-brand-olive leading-none -mt-2 ml-1">
              Apartelle
            </span>
          </div>
        </Link>

        {/* Desktop Links */}
        <div className="hidden lg:flex flex-grow justify-center items-center space-x-12 pl-4">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`relative pb-2 text-[13px] font-medium uppercase tracking-[0.15em] transition-all duration-300 ${
                  isActive 
                    ? 'text-brand-olive' 
                    : 'text-brand-olive hover:opacity-100 hover:translate-y-[-2px]'
                }`}
              >
                {link.name}
                {isActive && (
                  <motion.span
                    layoutId="navUnderline"
                    className="absolute bottom-0 left-0 w-full h-[2px] bg-brand-olive"
                  />
                )}
              </Link>
            );
          })}
        </div>

        {/* Desktop Book Now Button */}
        <div className="hidden lg:block shrink-0">
          <Link
            to="/rates"
            className="px-8 py-3.5 bg-brand-olive text-white text-[13px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 shadow-md hover:bg-brand-dark hover:scale-105 hover:shadow-xl active:scale-95 inline-block"
          >
            BOOK NOW
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden p-2 text-brand-olive hover:scale-110 transition-transform"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="text-3xl" />
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="absolute top-[100px] left-0 right-0 lg:hidden bg-brand-cream border-b border-brand-olive/20 text-brand-olive overflow-hidden shadow-xl"
          >
            <div className="flex flex-col p-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-sm font-bold uppercase tracking-[0.2em] border-b border-brand-olive/10 pb-3 transition-colors ${
                    location.pathname === link.path 
                      ? 'text-brand-olive' 
                      : 'text-brand-olive/70 hover:text-brand-olive'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/rates"
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-brand-olive text-white text-center py-4 font-bold uppercase tracking-[0.2em] text-xs mt-4 transition-all hover:bg-brand-dark"
              >
                Book Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;